import { oc } from "@orpc/contract";
import { SupplyNetworkDocumentSchema } from "@erp/shared-types";
import { z } from "zod";

// ─── Schemas ────────────────────────────────────────────────────────────────

export const SupplyNetworkVersionEntrySchema = z.object({
  id: z.string().uuid(),
  number: z.number().int(),
  status: z.enum(["draft", "finalized"]),
  parentId: z.string().uuid().nullable(),
  authorId: z.string().uuid().nullable(),
  /** Null for a version no person wrote, such as a conversion. */
  authorName: z.string().nullable(),
  createdAt: z.string(),
  finalizedAt: z.string().nullable(),
  changelog: z.string().nullable(),
});

/** Master data the network reads but never edits. */
export const SupplyNetworkMasterDataSchema = z.object({
  divisions: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  locations: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      divisionId: z.string().uuid().nullable(),
      city: z.string().nullable(),
      region: z.string().nullable(),
    }),
  ),
});

export const SupplyNetworkStateSchema = z.object({
  /** Null until the organization's network is first created. */
  networkId: z.string().uuid().nullable(),
  publishedVersionId: z.string().uuid().nullable(),
  /** Newest first. */
  versions: z.array(SupplyNetworkVersionEntrySchema),
  masterData: SupplyNetworkMasterDataSchema,
});

export const GetSupplyNetworkVersionInputSchema = z.object({
  versionId: z.string().uuid(),
});

export const SupplyNetworkVersionDetailSchema = z.object({
  version: SupplyNetworkVersionEntrySchema,
  document: SupplyNetworkDocumentSchema,
  /** Item names for the SKUs the document's item rules name. */
  itemNames: z.record(z.string(), z.string()),
});

export const CreateSupplyNetworkDraftInputSchema = z.object({
  /** The finalized version to start from. Defaults to the published one. */
  sourceVersionId: z.string().uuid().optional(),
});

export const CreateSupplyNetworkDraftResponseSchema = z.object({
  version: SupplyNetworkVersionEntrySchema,
  /** True when the caller already had a draft open, which is returned instead. */
  resumed: z.boolean(),
});

export const SaveSupplyNetworkDraftInputSchema = z.object({
  versionId: z.string().uuid(),
  document: SupplyNetworkDocumentSchema,
});

export const DeleteSupplyNetworkDraftInputSchema = z.object({
  versionId: z.string().uuid(),
});

export const SupplyNetworkStaleBaseWarningSchema = z.object({
  kind: z.literal("stale_base"),
  draftParentVersionNumber: z.number().int().nullable(),
  publishedVersionNumber: z.number().int().nullable(),
  message: z.string(),
});

export const FinalizeSupplyNetworkDraftInputSchema = z.object({
  versionId: z.string().uuid(),
  changelog: z.string().trim().min(1).max(4000),
  /** Publish in the same step. Needs the Approve permission. */
  publish: z.boolean().optional().default(false),
  acknowledgeStaleBase: z.boolean().optional().default(false),
});

export const FinalizeSupplyNetworkDraftResponseSchema = z.discriminatedUnion(
  "ok",
  [
    z.object({ ok: z.literal(true), version: SupplyNetworkVersionEntrySchema }),
    z.object({
      ok: z.literal(false),
      warning: SupplyNetworkStaleBaseWarningSchema,
    }),
  ],
);

export const PublishSupplyNetworkVersionInputSchema = z.object({
  versionId: z.string().uuid(),
  acknowledgeStaleBase: z.boolean().optional().default(false),
});

export const PublishSupplyNetworkVersionResponseSchema = z.discriminatedUnion(
  "ok",
  [
    z.object({ ok: z.literal(true) }),
    z.object({
      ok: z.literal(false),
      warning: SupplyNetworkStaleBaseWarningSchema,
    }),
  ],
);

// ─── Contracts ──────────────────────────────────────────────────────────────

export const supplyNetworkContracts = {
  getState: oc.output(SupplyNetworkStateSchema).route({
    method: "GET",
    summary: "Get the supply network's versions",
    description:
      "Every version of the organization's supply network, newest first, which one is published, and the master data (divisions and locations) the network is drawn over.",
  }),
  getVersion: oc
    .input(GetSupplyNetworkVersionInputSchema)
    .output(SupplyNetworkVersionDetailSchema)
    .route({
      method: "GET",
      summary: "Get one supply network version",
      description:
        "One version's stored document: roles, location settings, lanes, the sibling rule and item rules.",
    }),
  createDraft: oc
    .input(CreateSupplyNetworkDraftInputSchema)
    .output(CreateSupplyNetworkDraftResponseSchema)
    .route({
      summary: "Start a supply network draft",
      description:
        "Start a draft from a finalized version (the published one by default), or resume the caller's open draft. A network has one open draft at a time.",
    }),
  saveDraft: oc
    .input(SaveSupplyNetworkDraftInputSchema)
    .output(SupplyNetworkVersionEntrySchema)
    .route({
      summary: "Save a supply network draft",
      description:
        "Replace the caller's draft document. Saving never changes what the replenishment engine uses.",
    }),
  deleteDraft: oc
    .input(DeleteSupplyNetworkDraftInputSchema)
    .output(z.object({ ok: z.literal(true) }))
    .route({
      summary: "Delete a supply network draft",
      description:
        "Discard a draft. Its author can delete it, and so can anyone who may publish.",
    }),
  finalize: oc
    .input(FinalizeSupplyNetworkDraftInputSchema)
    .output(FinalizeSupplyNetworkDraftResponseSchema)
    .route({
      summary: "Finalize a supply network draft",
      description:
        "Validate the caller's draft against master data and freeze it with a changelog, optionally publishing it. Warns when the draft was started from a version that is no longer published.",
    }),
  publish: oc
    .input(PublishSupplyNetworkVersionInputSchema)
    .output(PublishSupplyNetworkVersionResponseSchema)
    .route({
      summary: "Publish a supply network version",
      description:
        "Make a finalized version the one the replenishment engine uses from its next run. Publishing an older version is how a change is rolled back.",
    }),
} as const;

export type SupplyNetworkVersionEntry = z.infer<
  typeof SupplyNetworkVersionEntrySchema
>;
export type SupplyNetworkMasterData = z.infer<
  typeof SupplyNetworkMasterDataSchema
>;
export type SupplyNetworkState = z.infer<typeof SupplyNetworkStateSchema>;
export type SupplyNetworkVersionDetail = z.infer<
  typeof SupplyNetworkVersionDetailSchema
>;
export type SupplyNetworkStaleBaseWarning = z.infer<
  typeof SupplyNetworkStaleBaseWarningSchema
>;
export type FinalizeSupplyNetworkDraftResponse = z.infer<
  typeof FinalizeSupplyNetworkDraftResponseSchema
>;
export type PublishSupplyNetworkVersionResponse = z.infer<
  typeof PublishSupplyNetworkVersionResponseSchema
>;
